<template>
  <div class="c-kdo-defaults">
    <n-loader :loading="$var('loadDefaults')" />

    <div class="kdo-toolbar">
      <div class="kdo-hint grow">
        Типовые документы — готовые наборы «docx + маршрут + поля». В карточке компании их можно создать одним нажатием.
        Чтобы каталог попал на другие стенды, выгрузите его в репозиторий:
        <span class="kdo-macro">php artisan kdo:export-default-templates</span> и закоммитьте изменения.
      </div>
      <button type="button" class="kdo-btn primary" @click="openEditor(null)">+ Добавить типовой документ</button>
    </div>

    <div class="kdo-card">
      <table>
        <thead>
          <tr>
            <th>Название</th>
            <th>Тип документа</th>
            <th>Файл</th>
            <th>Полей</th>
            <th>Статус</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="item in rows" :key="item.id">
            <td>
              <div>{{ item.title }}</div>
              <div class="sub">{{ item.description }}</div>
            </td>
            <td><span class="kdo-badge" :class="item.type === 'custom' ? 'muted' : 'ok'">{{ typeTitle(item.type) }}</span></td>
            <td>{{ item.fileName }}</td>
            <td>{{ item.assignedMetaFields.length }}</td>
            <td><span class="kdo-badge" :class="item.isActive ? 'ok' : 'muted'">{{ item.isActive ? 'Доступен' : 'Скрыт' }}</span></td>
            <td class="actions">
              <button type="button" class="kdo-btn small" @click="openEditor(item)">Изменить</button>
              <button type="button" class="kdo-btn small danger" @click="toDelete = item">Удалить</button>
            </td>
          </tr>
          <tr v-if="!rows.length">
            <td colspan="6" class="empty">Каталог пуст</td>
          </tr>
        </tbody>
      </table>
    </div>

    <div v-if="form" class="kdo-overlay" @mousedown.self="closeEditor">
      <div class="kdo-dialog wide">
        <h3>{{ form.id ? 'Изменить типовой документ' : 'Новый типовой документ' }}</h3>
        <div class="kdo-hint">
          Исполнители шагов «конкретный сотрудник» не хранятся: при создании шаблона для компании подставляется её руководитель.
        </div>

        <div class="kdo-form">
          <div class="row">
            <div>
              <label class="caption">Название</label>
              <input v-model="form.title" type="text" placeholder="Приказ о предоставлении отпуска">
            </div>
            <div>
              <label class="caption">Тип документа</label>
              <select v-model="form.type">
                <option v-for="type in dict.approvalTypes" :key="type.key" :value="type.key">{{ type.title }}</option>
              </select>
            </div>
          </div>
          <div class="row one">
            <div>
              <label class="caption">Описание (показывается в списке типовых)</label>
              <textarea v-model="form.description" rows="2"></textarea>
            </div>
          </div>

          <div class="section-title">Маршрут согласования</div>
          <div v-for="(step, index) in form.steps" :key="index" class="step-card">
            <div class="row">
              <div class="stage">
                <label class="caption">Этап</label>
                <input v-model.number="step.stage" type="number" min="0" max="50">
              </div>
              <div>
                <label class="caption">Название шага</label>
                <input v-model="step.title" type="text">
              </div>
            </div>
            <div class="row">
              <div>
                <label class="caption">Кто выполняет</label>
                <select v-model="step.approvalType">
                  <option v-for="type in dict.stepTypes" :key="type.key" :value="type.key">{{ type.title }}</option>
                </select>
              </div>
            </div>
            <div class="step-bottom">
              <label class="check"><input v-model="step.requireNCA" type="checkbox"> Требуется подпись ЭЦП</label>
              <label class="check"><input v-model="step.isActive" type="checkbox"> Шаг активен</label>
              <span class="spacer"></span>
              <button type="button" class="icon-btn" :disabled="index === 0" @click="moveStep(index, -1)">↑</button>
              <button type="button" class="icon-btn" :disabled="index === form.steps.length - 1" @click="moveStep(index, 1)">↓</button>
              <button type="button" class="icon-btn danger" :disabled="form.steps.length === 1" @click="form.steps.splice(index, 1)">✕</button>
            </div>
          </div>
          <button type="button" class="kdo-btn small" @click="addStep">+ Добавить шаг</button>

          <div class="row one" style="margin-top: 16px">
            <div>
              <label class="caption">Файл шаблона (.docx)</label>
              <div class="file-row">
                <label class="kdo-btn small file-button">
                  {{ uploading ? 'Загружаем...' : (form.fileId || form.fileName ? 'Заменить файл' : 'Выбрать файл') }}
                  <input type="file" accept=".docx" :disabled="uploading" @change="onFile">
                </label>
                <span v-if="form.fileName" class="file-name">{{ form.fileName }}</span>
                <span v-else class="note">Файл не выбран</span>
              </div>
            </div>
          </div>

          <div class="columns">
            <div class="column">
              <div class="section-title">Поля общей библиотеки</div>
              <input v-model="fieldSearch" class="mini-search" type="text" placeholder="Поиск поля">
              <div class="pick-list">
                <div v-for="field in availableFields" :key="field.key" class="pick-item" @click="addField(field.key)">
                  <span class="kdo-macro">{{ field.macro }}</span>
                  <span class="pick-label">{{ field.label }}</span>
                  <span class="plus">+</span>
                </div>
                <div v-if="!availableFields.length" class="note">Все поля добавлены или ничего не найдено</div>
              </div>
            </div>
            <div class="column">
              <div class="section-title">Поля документа (в порядке на форме)</div>
              <div class="pick-list">
                <div v-for="(key, index) in form.assignedMetaFields" :key="key" class="pick-item assigned">
                  <span class="kdo-macro">${{ '{' + key + '}' }}</span>
                  <span class="pick-label">{{ fieldLabel(key) }}</span>
                  <button type="button" class="icon-btn" :disabled="index === 0" @click="moveField(index, -1)">↑</button>
                  <button
                    type="button"
                    class="icon-btn"
                    :disabled="index === form.assignedMetaFields.length - 1"
                    @click="moveField(index, 1)">
                    ↓
                  </button>
                  <button type="button" class="icon-btn danger" @click="removeField(index)">✕</button>
                </div>
                <div v-if="!form.assignedMetaFields.length" class="note">Добавьте поля слева</div>
              </div>
            </div>
          </div>

          <div class="section-title">Сверка макросов в файле</div>
          <div v-if="!form.fileId" class="note">
            {{ form.id ? 'Загрузите файл заново, чтобы увидеть сверку макросов.' : 'Загрузите файл, чтобы увидеть, какие макросы в нём есть.' }}
          </div>
          <div v-else-if="analyzing" class="note">Проверяем файл...</div>
          <div v-else-if="analysis">
            <div v-for="item in analysis.macros" :key="item.key" class="macro-row">
              <span class="kdo-macro">{{ item.macro }}</span>
              <span class="kdo-badge" :class="statusClass(item.status)">{{ statusTitle(item.status) }}</span>
              <span class="macro-title">{{ item.title }}</span>
              <span v-if="item.note" class="note inline">{{ item.note }}</span>
              <button
                v-if="item.status === 'fieldNotAssigned'"
                type="button"
                class="kdo-btn small"
                @click="addField(item.key)">
                Добавить в документ
              </button>
              <button
                v-if="item.status === 'autofillUnavailable' && item.requires"
                type="button"
                class="kdo-btn small"
                @click="addField(item.requires)">
                Добавить поле «{{ fieldLabel(item.requires) }}»
              </button>
            </div>
            <div v-if="analysis.unusedFields.length" class="note" style="margin-top: 8px">
              Поля, которых нет в файле: {{ analysis.unusedFields.map((item) => item.title).join(', ') }}
            </div>
          </div>

          <div class="row" style="margin-top: 16px">
            <div style="display: flex; align-items: flex-end">
              <label class="check"><input v-model="form.isActive" type="checkbox"> Доступен для создания в компаниях</label>
            </div>
          </div>

          <div v-if="error" class="error">{{ error }}</div>
        </div>

        <div class="kdo-actions">
          <button type="button" class="kdo-btn" :disabled="saving" @click="closeEditor">Отмена</button>
          <button type="button" class="kdo-btn primary" :disabled="saving || uploading" @click="save">
            {{ saving ? 'Сохраняем...' : 'Сохранить' }}
          </button>
        </div>
      </div>
    </div>

    <c-modal-message
      v-if="toDelete"
      :title="`Удалить «${toDelete.title}» из каталога?`"
      :description="deleteDescription"
      @agree="remove"
      @cancel="toDelete = null" />
  </div>
</template>

<script>
export default {
  name: 'CKdoDefaultTemplates',
  data() {
    return {
      rows: [],
      dict: { approvalTypes: [], stepTypes: [], },
      fields: [],
      form: null,
      fieldSearch: '',
      uploading: false,
      analyzing: false,
      analysis: null,
      analyzeTimer: null,
      saving: false,
      error: '',
      toDelete: null,
    }
  },
  computed: {
    deleteDescription() {
      return 'Шаблоны, уже созданные для компаний, останутся. Чтобы документ пропал и на других стендах, ' +
        'после удаления выполните php artisan kdo:export-default-templates и закоммитьте изменения.'
    },
    availableFields() {
      if (!this.form) return []
      const query = this.fieldSearch.trim().toLowerCase()
      return this.fields.filter((field) => field.isActive && !this.form.assignedMetaFields.includes(field.key) &&
        (!query || field.key.toLowerCase().includes(query) || field.label.toLowerCase().includes(query)))
    },
  },
  created() {
    $api.kdo.dictionaries().then((response) => {
      this.dict = response.data.content
    })
    $api.kdo.fields(null).then((response) => {
      this.fields = response.data.content || []
    })
    this.load()
  },
  methods: {
    load() {
      this.$var('loadDefaults', true)
      $api.kdo.defaultTemplates().then((response) => {
        this.rows = response.data.content || []
      }).finally(() => {
        this.$var('loadDefaults', false)
      })
    },
    typeTitle(key) {
      const item = $n.find(this.dict.approvalTypes, (type) => type.key === key)
      return item ? item.title.replace(/ \(.*\)$/, '') : key
    },
    fieldLabel(key) {
      const field = $n.find(this.fields, (item) => item.key === key)
      return field ? field.label : key
    },
    emptyStep(stage) {
      return { title: '', description: '', stage, approvalType: 'exactUser', requireNCA: true, isActive: true, }
    },
    openEditor(item) {
      this.error = ''
      this.analysis = null
      this.fieldSearch = ''
      if (item) {
        this.form = {
          id: item.id,
          title: item.title,
          description: item.description || '',
          type: item.type,
          steps: item.steps.map((step) => ({ ...step, })),
          assignedMetaFields: item.assignedMetaFields.slice(),
          fileId: null,
          fileName: item.fileName,
          isActive: item.isActive,
        }
      } else {
        this.form = {
          id: null,
          title: '',
          description: '',
          type: 'custom',
          steps: [ this.emptyStep(0), ],
          assignedMetaFields: [],
          fileId: null,
          fileName: '',
          isActive: true,
        }
      }
    },
    closeEditor() {
      if (this.saving) return
      this.form = null
    },
    addStep() {
      const stages = this.form.steps.map((step) => Number(step.stage) || 0)
      this.form.steps.push(this.emptyStep(stages.length ? Math.max(...stages) + 1 : 0))
    },
    moveStep(index, shift) {
      const steps = this.form.steps
      const target = index + shift
      if (target < 0 || target >= steps.length) return
      const [ moved, ] = steps.splice(index, 1)
      steps.splice(target, 0, moved)
    },
    addField(key) {
      if (!this.form.assignedMetaFields.includes(key)) {
        this.form.assignedMetaFields.push(key)
        this.scheduleAnalyze()
      }
    },
    removeField(index) {
      this.form.assignedMetaFields.splice(index, 1)
      this.scheduleAnalyze()
    },
    moveField(index, shift) {
      const items = this.form.assignedMetaFields
      const target = index + shift
      if (target < 0 || target >= items.length) return
      const [ moved, ] = items.splice(index, 1)
      items.splice(target, 0, moved)
    },
    onFile(event) {
      const file = event.target.files[0]
      event.target.value = ''
      if (!file) return
      this.error = ''
      this.uploading = true
      $api.kdo.uploadDefaultTemplateFile(file).then((response) => {
        const uploaded = response.data.content
        this.form.fileId = uploaded.id
        this.form.fileName = uploaded.name
        this.scheduleAnalyze()
      }).catch((err) => {
        this.error = err?.response?.data?.message || 'Не удалось загрузить файл'
      }).finally(() => {
        this.uploading = false
      })
    },
    scheduleAnalyze() {
      clearTimeout(this.analyzeTimer)
      this.analyzeTimer = setTimeout(() => this.analyze(), 300)
    },
    analyze() {
      if (!this.form || !this.form.fileId) {
        this.analysis = null
        return
      }
      this.analyzing = true
      $api.kdo.analyzeLibrary({ fileId: this.form.fileId, assignedMetaFields: this.form.assignedMetaFields, }).then((response) => {
        this.analysis = response.data.content
      }).catch(() => {
        this.analysis = null
      }).finally(() => {
        this.analyzing = false
      })
    },
    statusTitle(status) {
      return {
        field: 'Поле документа',
        autofill: 'Автозаполнение',
        builtIn: 'Встроенный',
        fieldNotAssigned: 'Поле не добавлено',
        autofillUnavailable: 'Нет источника',
        unknown: 'Не найден',
      }[status] || status
    },
    statusClass(status) {
      return { field: 'ok', autofill: 'ok', builtIn: 'ok', fieldNotAssigned: 'warn', autofillUnavailable: 'warn', unknown: 'err', }[status]
    },
    save() {
      this.error = ''
      this.saving = true
      const payload = {
        title: this.form.title,
        description: this.form.description,
        type: this.form.type,
        steps: this.form.steps,
        assignedMetaFields: this.form.assignedMetaFields,
        isActive: this.form.isActive,
      }
      // файл отправляем только если его загрузили заново
      if (this.form.fileId) payload.fileId = this.form.fileId
      const request = this.form.id ?
        $api.kdo.editDefaultTemplate(this.form.id, payload) :
        $api.kdo.createDefaultTemplate(payload)
      request.then(() => {
        this.form = null
        this.load()
      }).catch((err) => {
        this.error = err?.response?.data?.message || 'Не удалось сохранить типовой документ'
      }).finally(() => {
        this.saving = false
      })
    },
    remove() {
      const item = this.toDelete
      this.toDelete = null
      this.$var('loadDefaults', true)
      $api.kdo.deleteDefaultTemplate(item.id).then(() => {
        $app.ui.notify('Типовой документ удалён из каталога', 'success')
        this.load()
      }).catch(() => {
        this.$var('loadDefaults', false)
      })
    },
  },
}
</script>

<style lang="scss" scoped>
@import './kdo.scss';

.grow {
  flex: 1;
  margin: 0;
}

.sub {
  color: #8f88a8;
  font-size: 12px;
  margin-top: 2px;
  max-width: 420px;
}

.actions {
  white-space: nowrap;
  text-align: right;

  .kdo-btn {
    margin-left: 6px;
  }
}

.file-row {
  display: flex;
  align-items: center;
  gap: 12px;

  .file-button {
    position: relative;
    display: inline-flex;
    align-items: center;
    cursor: pointer;

    input {
      position: absolute;
      inset: 0;
      opacity: 0;
      cursor: pointer;
    }
  }

  .file-name {
    font-weight: 600;
    color: #2A2766;
  }
}

.columns {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 18px;

  @media (max-width: 800px) {
    grid-template-columns: 1fr;
  }

  .mini-search {
    margin-bottom: 8px;
  }
}

.pick-list {
  max-height: 260px;
  overflow-y: auto;
  border: 1px solid #ece4fb;
  border-radius: 12px;
  background: #faf8ff;
  padding: 6px;
}

.pick-item {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 6px 8px;
  border-radius: 8px;
  cursor: pointer;

  &:hover {
    background: #f0e9ff;
  }

  &.assigned {
    cursor: default;

    &:hover {
      background: transparent;
    }
  }

  .pick-label {
    flex: 1;
    font-size: 13px;
    min-width: 0;
  }

  .plus {
    color: var(--primary);
    font-weight: 700;
  }

  .icon-btn {
    width: 26px;
    height: 26px;
    min-width: 26px;
  }
}

.macro-row {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 10px;
  padding: 6px 0;
  border-bottom: 1px solid #f0ecf8;

  .macro-title {
    font-size: 13px;
  }

  .note.inline {
    margin: 0;
    flex: 1;
  }
}

.step-card {
  border: 1px solid #ece4fb;
  border-radius: 14px;
  background: #faf8ff;
  padding: 14px 14px 4px;
  margin-bottom: 12px;

  .row:first-child {
    grid-template-columns: 110px 1fr;
  }

  .step-bottom {
    display: flex;
    align-items: center;
    gap: 14px;
    margin: 4px 0 10px;

    .spacer {
      flex: 1;
    }
  }
}
</style>
